import { HalConfig } from '@apparatix/types/HalOptions';
import { useHalFeatures } from '@apparatix/stores/useHalFeatures';
import { useUtilities } from './useUtilities';

export const useHalConfig = () => {
  const store = useHalFeatures();
  const { arrayHasValues } = useUtilities();

  const hasConfig = (config: HalConfig) => store.hasConfig(config);
  const hasAnyConfig = (configs: HalConfig[]) => configs.some(config => store.hasConfig(config));
  const hasAllConfigs = (configs: HalConfig[]) => configs.every(config => store.hasConfig(config));

  const ensureConfigArray = (config: HalConfig | HalConfig[]) => Array.isArray(config) ? config : [config];

  /**
   *
   * @param execute function to execute if the provided flags allow
   * @param include if provided, user must have ALL provided flags in order to execute
   * @param exclude if provided the user must not have ANY of the provided flags to execute. Excludes take precedence over includes
   * @returns true if the function was executed
   */
  const conditionallyExecute = (execute: () => void, include?: HalConfig | HalConfig, exclude?: HalConfig | HalConfig[]) => {
    if (typeof execute !== 'function') {
      throw new Error('Invalid argument for execute callback');
    }

    const toInclude = ensureConfigArray(include);
    const toExclude = ensureConfigArray(exclude);

    if (arrayHasValues(toExclude) && !hasAnyConfig(toExclude)) {
      execute();
      return true;
    }

    if (arrayHasValues(toInclude) && hasAllConfigs(toInclude)) {
      execute();
      return true;
    }

    return false;
  };

  return {
    conditionallyExecute,
    hasAllConfigs,
    hasAnyConfig,
    hasConfig,
  };
};
